import React from "react"
import {
  Dialog,
  DialogContent,
  RadioGroup,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { ViraButton } from "../atoms/ViraButton"
import { ViraIconButton } from "../atoms/ViraIconButton"
import { ViraIcon } from "../atoms/ViraIcon"
import { ViraRadio } from "../atoms/ViraRadio"
import { ViraTextField } from "../atoms/ViraTextField"
import { ViraText, Heading } from "../atoms/ViraText"
import { DayTabs, type DayOfWeek } from "../molecules/DayTabs"
import { typeScale, typography } from "../theme/tokens"

export type CustomRecurrenceEnds =
  | { type: "never" }
  | { type: "on"; date: string }
  | { type: "after"; count: number }

export type CustomRecurrenceValue = {
  interval: number
  unit: "week"
  days: DayOfWeek[]
  ends: CustomRecurrenceEnds
}

export type CustomRecurrenceDialogProps = {
  open: boolean
  value: CustomRecurrenceValue
  onChange?: (value: CustomRecurrenceValue) => void
  onCancel?: () => void
  onDone?: (value: CustomRecurrenceValue) => void
  /**
   * When false, hide end-date / occurrence controls (Hasura weekday schedules
   * cannot persist an end condition). Defaults to true.
   */
  showEnds?: boolean
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: typography.fontFamily.body,
    fontSize: typeScale.body,
  },
  "& .MuiInputLabel-root": {
    fontFamily: typography.fontFamily.body,
    fontSize: typeScale.body,
  },
} as const

const sectionLabelSx = {
  fontFamily: typography.fontFamily.body,
  fontSize: typeScale.meta,
  fontWeight: 500,
  lineHeight: 1.3,
  mb: 1,
} as const

/**
 * Google Calendar–style custom recurrence (week + days + ends).
 * Delivery supports weekly weekday flags only — interval/unit are fixed.
 */
export function CustomRecurrenceDialog({
  open,
  value,
  onChange,
  onCancel,
  onDone,
  showEnds = true,
}: CustomRecurrenceDialogProps) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))

  const update = (patch: Partial<CustomRecurrenceValue>) => {
    onChange?.({ ...value, ...patch })
  }

  const endsType = value.ends.type
  const endsOnDate = value.ends.type === "on" ? value.ends.date : ""
  const endsAfterCount =
    value.ends.type === "after" ? value.ends.count : 13

  // Allow clearing the field while typing (don't force min=1 on every keystroke).
  const [afterDraft, setAfterDraft] = React.useState(String(endsAfterCount))
  React.useEffect(() => {
    if (endsType === "after") {
      setAfterDraft(String(endsAfterCount))
    }
  }, [endsType, endsAfterCount])

  const commitAfterCount = (raw: string) => {
    const parsed = Number.parseInt(raw, 10)
    const count = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1
    setAfterDraft(String(count))
    update({ ends: { type: "after", count } })
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
          m: fullScreen ? 0 : undefined,
          borderRadius: fullScreen ? 0 : undefined,
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, position: "relative" }}>
        <ViraIconButton
          aria-label="Close"
          onClick={onCancel}
          sx={{ position: "absolute", top: 12, right: 12, width: 40, height: 40 }}
        >
          <ViraIcon name="close" tone="muted" fontSize="small" />
        </ViraIconButton>

        <Heading
          level="card"
          sx={{
            mb: 3,
            pr: 5,
            fontFamily: typography.fontFamily.display,
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
        >
          Custom recurrence
        </Heading>

        <ViraText tone="muted" sx={sectionLabelSx}>
          Repeat every
        </ViraText>
        <ViraText
          tone="ink"
          sx={{
            fontFamily: typography.fontFamily.body,
            fontSize: typeScale.body,
            fontWeight: 600,
            mb: 3,
          }}
        >
          Week
        </ViraText>

        <ViraText tone="muted" sx={sectionLabelSx}>
          Repeat on
        </ViraText>
        <Stack sx={{ mb: 3 }}>
          <DayTabs
            selected={value.days}
            onChange={(days) => update({ days })}
            required
            aria-label="Days of the week"
          />
        </Stack>

        {showEnds ? (
          <>
            <ViraText tone="muted" sx={sectionLabelSx}>
              Ends
            </ViraText>
            <RadioGroup
              value={endsType}
              onChange={(_, next) => {
                if (next === "never") {
                  update({ ends: { type: "never" } })
                } else if (next === "on") {
                  update({
                    ends: {
                      type: "on",
                      date:
                        endsOnDate ||
                        new Date().toISOString().slice(0, 10),
                    },
                  })
                } else {
                  update({
                    ends: {
                      type: "after",
                      count: endsAfterCount,
                    },
                  })
                  setAfterDraft(String(endsAfterCount))
                }
              }}
              sx={{ mb: 3, gap: 0.5 }}
            >
              <ViraRadio value="never" label="Never" />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mb: 0.5 }}
              >
                <ViraRadio value="on" label="On" />
                <ViraTextField
                  size="small"
                  type="date"
                  disabled={endsType !== "on"}
                  value={endsOnDate}
                  onChange={(e) =>
                    update({ ends: { type: "on", date: e.target.value } })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, ...fieldSx }}
                  inputProps={{ "aria-label": "End on date" }}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ViraRadio value="after" label="After" />
                <ViraTextField
                  size="small"
                  type="number"
                  disabled={endsType !== "after"}
                  value={afterDraft}
                  onChange={(e) => {
                    const raw = e.target.value
                    setAfterDraft(raw)
                    if (raw === "") {
                      return
                    }
                    const parsed = Number.parseInt(raw, 10)
                    if (Number.isFinite(parsed) && parsed >= 1) {
                      update({ ends: { type: "after", count: parsed } })
                    }
                  }}
                  onBlur={() => commitAfterCount(afterDraft)}
                  sx={{ width: 96, ...fieldSx }}
                  inputProps={{ min: 1, "aria-label": "Occurrences" }}
                />
                <ViraText
                  tone="subtle"
                  sx={{
                    fontFamily: typography.fontFamily.body,
                    fontSize: typeScale.body,
                  }}
                >
                  occurrences
                </ViraText>
              </Stack>
            </RadioGroup>
          </>
        ) : null}

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <ViraButton variant="secondary" onClick={onCancel}>
            Cancel
          </ViraButton>
          <ViraButton
            variant="primary"
            onClick={() => {
              const committed =
                showEnds && endsType === "after"
                  ? {
                      ...value,
                      interval: 1 as const,
                      unit: "week" as const,
                      ends: {
                        type: "after" as const,
                        count: Math.max(
                          1,
                          Number.parseInt(afterDraft, 10) || 1
                        ),
                      },
                    }
                  : {
                      ...value,
                      interval: 1 as const,
                      unit: "week" as const,
                      ends: showEnds
                        ? value.ends
                        : ({ type: "never" } as const),
                    }
              onDone?.(committed)
            }}
            disabled={value.days.length === 0}
          >
            Done
          </ViraButton>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
